import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNotifications, markAllRead, markNotificationRead } from '../../store/slices/notificationSlice';

const BellIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return `${Math.floor(s / 604800)}w`;
};

// Map a notification to its in-app destination (web pages deep-link via query params).
const targetFor = (n) => {
  const id = n.relatedId;
  if (['follow', 'follow_accepted', 'follow_request'].includes(n.type) && n.fromUser) {
    return `/profile/${n.fromUser.username}`;
  }
  if (n.relatedType === 'event' && id) return `/events?event=${id}`;
  if (n.relatedType === 'post' && id) return `/feed?post=${id}`;
  if (n.relatedType === 'route' && id) return `/routes?route=${id}`;
  if (n.relatedType === 'landmark' && id) return `/routes?landmark=${id}`;
  // The server stores actionUrl '/messages/<id>', which the web router has no route
  // for — resolve it to the query form Messages.jsx reads (same shape as sw.js).
  if (n.relatedType === 'conversation' && id) return `/messages?c=${id}`;
  return n.actionUrl || null;
};

const NotificationBell = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, unreadCount } = useSelector(s => s.notifications);
  const [open, setOpen] = useState(false);
  const ref = useRef();

  // Poll for new notifications (same cadence as the messages badge).
  useEffect(() => {
    dispatch(fetchNotifications());
    const id = setInterval(() => dispatch(fetchNotifications()), 20000);
    return () => clearInterval(id);
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const onItem = (n) => {
    if (!n.isRead) dispatch(markNotificationRead(n.id));
    const target = targetFor(n);
    setOpen(false);
    if (target) navigate(target);
  };

  return (
    <div className="notif-bell-wrap" ref={ref}>
      <button className="notif-bell" onClick={() => setOpen(o => !o)} title="Notifications" aria-label="Notifications">
        <BellIcon />
        {unreadCount > 0 && <span className="navbar-msg-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-head">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button className="notif-mark-all" onClick={() => dispatch(markAllRead())}>Mark all read</button>
            )}
          </div>
          <div className="notif-dropdown-list">
            {(!items || items.length === 0) ? (
              <p className="notif-empty">No notifications yet.</p>
            ) : (
              items.map(n => (
                <button key={n.id} className={`notif-item${n.isRead ? '' : ' unread'}`} onClick={() => onItem(n)}>
                  <div className="notif-item-avatar">
                    {n.fromUser?.profileImageUrl
                      ? <img src={n.fromUser.profileImageUrl} alt="" />
                      : (n.fromUser?.username?.[0] || '•').toUpperCase()}
                  </div>
                  <div className="notif-item-body">
                    <span className="notif-item-text">{n.content}</span>
                    <span className="notif-item-time">{timeAgo(n.createdAt)}</span>
                  </div>
                  {!n.isRead && <span className="notif-item-dot" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
